import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { ShortUrl } from '@air/api';
import { ShortUrlObjectType } from '@air/api/types';
import { FIVE_MINUTES } from '@air/utilities';
import { callAirApi } from '@air/utils-api';

import { fetchPrivateWorkspaceAgentClipsListByIds } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { isWorkspaceAgentShareLinkMonikerUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentShareLinkMonikerUnresolvableError';
import { toWorkspaceAgentShareLinkMonikerSubject } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentShareLinkMonikerSubject';
import { getClipDisplayTitle } from '~/utils/getClipDisplayTitle';

const PILL_SHARE_LINK_TRANSIENT_RETRIES = 1;

export const PRIVATE_WORKSPACE_AGENT_SHARE_LINK_MONIKER_KEY = 'PRIVATE_WORKSPACE_AGENT_SHARE_LINK_MONIKER';

export type UsePrivateWorkspaceAgentShareLinkMonikerParams = {
  shortId: string | undefined;
};

export type PrivateWorkspaceAgentShareLinkMonikerKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SHARE_LINK_MONIKER_KEY,
  UsePrivateWorkspaceAgentShareLinkMonikerParams,
];

export const getPrivateWorkspaceAgentShareLinkMonikerKey = (
  params: UsePrivateWorkspaceAgentShareLinkMonikerParams,
): PrivateWorkspaceAgentShareLinkMonikerKey => [PRIVATE_WORKSPACE_AGENT_SHARE_LINK_MONIKER_KEY, params];

export const isPrivateWorkspaceAgentShareLinkMonikerKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentShareLinkMonikerKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_SHARE_LINK_MONIKER_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentShareLinkMonikerOptions = {
  /** Gated on the chip being in view, so a long history does not fan out a request per chip. */
  enabled?: boolean;
};

/**
 * Loads the title and preview fields for a history `@[shareLink:<shortId>]` chip. The wire moniker only carries a
 * short id, so resolution goes through `ShortUrl.getByShortId`, which returns the link target (board, clip,
 * form, canvas, etc.) and the metadata the preview card needs. When the public clip row has no display name, an
 * optional `Clips.listByIds` pass may fill the title; if that lookup fails, the chip still renders from the
 * `getByShortId` payload.
 */
export const fetchPrivateWorkspaceAgentShareLinkMoniker = async (shortId: string) => {
  const response = await callAirApi(() => ShortUrl.getByShortId(shortId));

  if (response.type === ShortUrlObjectType.clip || response.type === ShortUrlObjectType.asset) {
    const shortUrlClip = response.data;

    // `getByShortId` already carries thumbnail, type, ext, and usually `title` or `importedName`.
    // Only hit `listByIds` when the short-url clip row has no display name at all.
    if (!getClipDisplayTitle(shortUrlClip)) {
      const { id: clipId, workspaceId } = shortUrlClip;

      if (workspaceId && clipId) {
        try {
          const clips = await fetchPrivateWorkspaceAgentClipsListByIds({
            clipIds: [clipId],
            workspaceId,
          });
          const workspaceClip = clips.find((entry) => entry.id === clipId);

          if (workspaceClip) {
            return toWorkspaceAgentShareLinkMonikerSubject(response, workspaceClip);
          }
        } catch {
          // Enrichment is best-effort; a resolved short URL should still render.
        }
      }
    }
  }

  return toWorkspaceAgentShareLinkMonikerSubject(response);
};

/**
 * Built with `getPrivateWorkspaceAgentShareLinkMonikerKey`, so each history chip keeps its own cache entry.
 * `throwOnError: false`: a deleted or viewer-gated short id (`isWorkspaceAgentShareLinkMonikerUnresolvableError`)
 * renders an unavailable chip, not an error boundary; other errors may retry once.
 */
export const usePrivateWorkspaceAgentShareLinkMoniker = (
  { shortId }: UsePrivateWorkspaceAgentShareLinkMonikerParams,
  options?: UsePrivateWorkspaceAgentShareLinkMonikerOptions,
) =>
  useQuery({
    queryKey: getPrivateWorkspaceAgentShareLinkMonikerKey({ shortId }),
    queryFn: shortId ? () => fetchPrivateWorkspaceAgentShareLinkMoniker(shortId) : skipToken,
    enabled: !!shortId && (options?.enabled ?? true),
    staleTime: FIVE_MINUTES,
    retry: (failureCount, error) =>
      !isWorkspaceAgentShareLinkMonikerUnresolvableError(error) && failureCount < PILL_SHARE_LINK_TRANSIENT_RETRIES,
    refetchOnWindowFocus: (query) => !!query.state.error,
    throwOnError: false,
  });
